import { trackingInputModify, trackingInputPasteModify } from '@/utils';
import type { ChangeEvent, ClipboardEvent, Dispatch, KeyboardEvent, RefObject, SetStateAction } from 'react';

interface UseTrackingInputLogicProps {
  inputRef: RefObject<HTMLInputElement>;
  chips: string[];
  setChips: Dispatch<SetStateAction<string[]>>;
  maxChip?: number;
  isDisabled: boolean;
  onChange: Dispatch<SetStateAction<string>>;
}

interface UseTrackingInputLogicReturn {
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handlePaste: (e: ClipboardEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  processCurrentInput: () => string[];
}

export const useTrackingInputLogic = ({
  inputRef,
  chips,
  setChips,
  maxChip,
  isDisabled,
  onChange,
}: UseTrackingInputLogicProps): UseTrackingInputLogicReturn => {
  const isChipExist = (chipFragments: string[]): string[] | null => {
    const validChipFragments = chipFragments.filter((chipFragment) => !chips.find((chip) => chip === chipFragment));
    return validChipFragments.length > 0 ? validChipFragments : null;
  };

  const checkValidAndSetData = (chipFragments: string[]) => {
    const validChipFragments = isChipExist(chipFragments);
    validChipFragments && setChips(chips.concat(validChipFragments).slice(0, maxChip));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    isDisabled && onChange(e.target.value);
    const chipFragments = trackingInputModify(e.target.value);
    if (!chipFragments) {
      return;
    }

    if (chipFragments.length > 0) {
      checkValidAndSetData(chipFragments);
    }

    e.currentTarget.value = '';
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const chipFragments = trackingInputPasteModify(e.currentTarget.value + e.clipboardData.getData('Text'));

    if (chipFragments && chipFragments.length > 0) {
      checkValidAndSetData(chipFragments);
      e.currentTarget.value = '';
    }
  };

  const handleEnterAndOnClick = () => {
    if (!inputRef.current) {
      return;
    }
    const chipFragments = trackingInputPasteModify(inputRef.current.value);
    if (chipFragments && chipFragments.length > 0) {
      checkValidAndSetData(chipFragments);
      inputRef.current.value = '';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter') {
      handleEnterAndOnClick();
      return;
    }

    if (e.code !== 'Backspace' || !chips.length) {
      return;
    }

    !e.currentTarget.value && setChips(chips.slice(0, -1));
  };

  const processCurrentInput = (): string[] => {
    if (!inputRef.current) return chips;
    const chipFragments = trackingInputPasteModify(inputRef.current.value);
    if (chipFragments && chipFragments.length > 0) {
      const validChips = isChipExist(chipFragments);
      if (validChips) {
        const newChips = chips.concat(validChips).slice(0, maxChip);
        setChips(newChips);
        inputRef.current.value = '';
        return newChips;
      }
    }
    return chips;
  };

  return { handleChange, handlePaste, handleKeyDown, processCurrentInput };
};
